import React, { MouseEventHandler, useCallback, useEffect, useMemo, useState } from 'react'
import { css, SerializedStyles } from '@emotion/core'
import * as Button from '../atoms/button'

type DropDownItemProps = {
  title: string
  onClick: MouseEventHandler<HTMLLIElement>
  icon?: string
  disabled?: boolean
}

type DropDownProps = {
  title: string
  items: DropDownItemProps[]
  buttonStyle: Button.Props
  dropDownStyle?: SerializedStyles
}

export const DropDownButton = (props: DropDownProps) => {
  const [isDropDownOpen, setIsDropDownOpen] = useState<boolean>(false)

  const onClickOtherComponent = useCallback(() => setIsDropDownOpen(false), [])

  const buttonArrowIconMapping = useMemo(() => {
    switch (props.buttonStyle.buttonType) {
      case Button.ButtonType.Primary:
        return require('@/static/images/arrow_down_white.svg')
      default:
        return require('@/static/images/arrow_gray.svg')
    }
  }, [])

  useEffect(() => {
    return () => document.removeEventListener('click', onClickOtherComponent)
  }, [])

  useEffect(() => {
    // @see react16用 https://zenn.dev/co9xs/articles/react17-event-delegation
    if (isDropDownOpen) {
      document.addEventListener('click', onClickOtherComponent)
    } else {
      document.removeEventListener('click', onClickOtherComponent)
    }
  }, [isDropDownOpen])

  return (
    <div style={{ position: 'relative' }} onClick={e => e.stopPropagation()}>
      <Button.Button
        {...props.buttonStyle}
        icon={undefined}
        onClick={() => setIsDropDownOpen(!isDropDownOpen)}
        customCss={css({ padding: '0 16px', justifyContent: 'space-between' })}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {props.buttonStyle.icon && <img src={props.buttonStyle.icon} style={{ width: 14, marginRight: 7 }} />}
          <div>{props.title}</div>
        </div>
        <img src={buttonArrowIconMapping} style={{ padding: '4px 0' }} />
      </Button.Button>
      <div
        css={[dropDownStyle, css({ top: Number(props.buttonStyle.height || 32) + 4 }), props.dropDownStyle]}
        style={{ display: isDropDownOpen ? 'block' : 'none' }}
      >
        <ul>
          {props.items.map(item => (
            <li
              key={item.title}
              css={dropDownContentStyle}
              onClick={e => !item.disabled && item.onClick(e)}
              className={item.disabled ? '-disabled' : ''}
            >
              <p>{item.title}</p>
              {item.icon && <img css={suffixIconStyle} src={item.icon} alt="" />}
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

const suffixIconStyle = css({
  position: 'absolute',
  right: 0,
  padding: '4px 8px',
})

const dropDownStyle = css({
  width: 140,
  zIndex: 50,
  backgroundColor: '#FFF',
  position: 'absolute',
  boxShadow: '0px 3px 10px #0000003d',
  borderRadius: 5,
})

const dropDownContentStyle = css({
  display: 'flex',
  height: 34,
  listStyle: 'none',
  alignItems: 'center',
  padding: '0 16px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  '&:hover': {
    background: '#f2f2f2',
  },
  p: {
    fontSize: 14,
    fontWeight: 'normal',
    color: '#272727',
  },
  '&.-disabled': {
    cursor: 'default',
    '&:hover': { background: 'none' },
    p: { color: 'rgb(204, 204, 204)' },
  },
})
